import { useCallback } from 'react'
import {
  useNavigate,
  type NavigateFunction,
  type NavigateOptions,
  type To,
} from 'react-router-dom'
import type { AuthUser, AuthUserClient } from '../types/auth.types'

const SESSION_LAST_TENANT_KEY = 'lastTenantId'
/**
 * Cross-tab mirror of the last tenant a tab settled on. sessionStorage is
 * per-tab, so a brand-new tab (middle-click, "open in new tab", pasted deep
 * link) starts with neither it nor the persisted selectedClientTenant (stripped
 * on rehydrate) and used to fall straight through to the user's FIRST enabled
 * client — usually not the tenant the record belongs to, which rendered a blank
 * page once the wrong tenant id was appended. Only a fallback hint: the URL
 * stays the source of truth and the value is re-validated against user.clients.
 */
const SHARED_LAST_TENANT_KEY = 'lastTenantIdShared'

export function userCanAccessTenantId(
  user: AuthUser,
  tenantId: number
): boolean {
  if (user.superAdmin) {
    if (tenantId === -1) return true
    return user.clients.some((c) => c.id === tenantId)
  }
  return user.clients.some((c) => c.enabled && c.id === tenantId)
}

export function getDefaultTenantIdForUser(user: AuthUser): number {
  if (user.superAdmin) {
    const nonGlobal = user.clients.find((c) => c.id !== -1)
    return nonGlobal?.id ?? -1
  }
  const enabled = user.clients.find((c) => c.enabled)
  return enabled?.id ?? user.clients[0]?.id
}

export function getSessionLastTenantId(): number | null {
  try {
    const raw = sessionStorage.getItem(SESSION_LAST_TENANT_KEY)
    if (raw == null || raw === '') return null
    const n = parseInt(raw, 10)
    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}

export function getSharedLastTenantId(): number | null {
  try {
    const raw = localStorage.getItem(SHARED_LAST_TENANT_KEY)
    if (raw == null || raw === '') return null
    const n = parseInt(raw, 10)
    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}

export function setSessionLastTenantId(tenantId: number): void {
  try {
    sessionStorage.setItem(SESSION_LAST_TENANT_KEY, String(tenantId))
  } catch (err) {
    console.error(err)
  }
  try {
    localStorage.setItem(SHARED_LAST_TENANT_KEY, String(tenantId))
  } catch {
    /* storage blocked — the per-tab value above is enough */
  }
}

export function clearSessionLastTenantId(): void {
  try {
    sessionStorage.removeItem(SESSION_LAST_TENANT_KEY)
  } catch (err) {
    console.error(err)
  }
  try {
    localStorage.removeItem(SHARED_LAST_TENANT_KEY)
  } catch {
    /* ignore */
  }
}

export function resolveBootstrapTenantId(
  user: AuthUser,
  selectedClient: AuthUserClient | null
): number {
  if (
    selectedClient?.id != null &&
    userCanAccessTenantId(user, selectedClient.id)
  ) {
    return selectedClient.id
  }
  const remembered = getSessionLastTenantId()
  if (remembered != null && userCanAccessTenantId(user, remembered)) {
    return remembered
  }
  const shared = getSharedLastTenantId()
  if (shared != null && userCanAccessTenantId(user, shared)) {
    return shared
  }
  return getDefaultTenantIdForUser(user)
}

export function getTenantIdFromPathname(pathname: string): number | null {
  const segments = pathname.split('/').filter(Boolean)
  const first = segments[0]
  if (!first) return null
  const n = parseInt(first, 10)
  return Number.isFinite(n) ? n : null
}

export function getTenantIdFromWindow(): number | null {
  if (typeof window === 'undefined') return null
  return getTenantIdFromPathname(window.location.pathname)
}

/** Tenant id from Admin Client Setup URLs: `/clients/client/:tenantid/...` */
export function getClientConfigTenantIdFromPathname(
  pathname: string
): number | null {
  const match = pathname.match(/^\/clients\/client\/(\d+)(?:\/|$)/)
  if (!match) return null
  const n = parseInt(match[1], 10)
  return Number.isFinite(n) ? n : null
}

export function stripTenantIdPrefix(pathname: string): string {
  const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`
  const segments = normalized.split('/').filter(Boolean)
  if (segments.length === 0) return '/'
  const maybeTenant = parseInt(segments[0], 10)
  if (!Number.isFinite(maybeTenant)) return normalized
  const rest = segments.slice(1).join('/')
  return rest ? `/${rest}` : '/'
}

/**
 * Paths that are valid WITHOUT a leading `/{tenantId}` segment in every portal.
 * Portal-specific ones (admin `/clients`) are added via
 * registerTenantOptionalPaths so there is ONE list that the route gates,
 * navigation helpers and client dropdown all consult — separate copies drifted
 * and the nav helpers rewrote `/clients/...` into `/{tid}/clients/...`, which
 * matches no route and renders a blank screen.
 */
const BASE_TENANT_OPTIONAL_PATHS = [
  '/logout',
  '/error',
  '/docuSign',
  '/thank-you-page',
]
let registeredTenantOptionalPaths: string[] = []

export function registerTenantOptionalPaths(prefixes: string[]): void {
  registeredTenantOptionalPaths = Array.from(
    new Set([...registeredTenantOptionalPaths, ...prefixes])
  )
}

export function isTenantOptionalPath(pathname: string): boolean {
  // React Router matches paths case-insensitively, so this must too — a
  // `/Clients/...` URL renders the clients route and must not be treated as
  // tenant-scoped.
  const normalized = (
    pathname.startsWith('/') ? pathname : `/${pathname}`
  ).toLowerCase()
  return [...BASE_TENANT_OPTIONAL_PATHS, ...registeredTenantOptionalPaths].some(
    (prefix) => {
      const p = prefix.toLowerCase()
      return (
        normalized === p ||
        normalized.startsWith(`${p}/`) ||
        normalized.startsWith(`${p}?`) ||
        normalized.startsWith(`${p}#`)
      )
    }
  )
}

export function withTenantIdInPathname(
  pathname: string,
  tenantId: number
): string {
  if (isTenantOptionalPath(pathname)) return pathname
  const base = stripTenantIdPrefix(pathname)
  if (base === '/') return `/${tenantId}`
  return `/${tenantId}${base.startsWith('/') ? base : `/${base}`}`
}

export function appendTenantIdToPath(
  path: string,
  preferredTenantId?: number | null
): string {
  const tid =
    typeof preferredTenantId === 'number' && Number.isFinite(preferredTenantId)
      ? preferredTenantId
      : getTenantIdFromWindow() ?? getSessionLastTenantId()
  if (tid == null) {
    // No tenant to apply — but still hand back a ROOTED path. Returning a bare
    // `entity/…` here let callers pass a relative URL to window.open, which the
    // browser resolves against the current page: from /1003 that produced
    // /entity/…  with the tenant silently dropped, and a blank tab.
    if (/^[a-z][a-z0-9+.-]*:/i.test(path) || path.startsWith('/')) return path
    return `/${path}`
  }
  if (/^https?:\/\//i.test(path)) {
    try {
      const u = new URL(path)
      u.pathname = withTenantIdInPathname(u.pathname, tid)
      return u.toString()
    } catch {
      return path
    }
  }
  const hashIndex = path.indexOf('#')
  const hash = hashIndex === -1 ? '' : path.slice(hashIndex)
  const noHash = hashIndex === -1 ? path : path.slice(0, hashIndex)
  const q = noHash.indexOf('?')
  const pathOnly = q === -1 ? noHash : noHash.slice(0, q)
  const searchOnly = q === -1 ? '' : noHash.slice(q)
  return `${withTenantIdInPathname(pathOnly || '/', tid)}${searchOnly}${hash}`
}

function shouldSkipTenantForPath(pathname: string): boolean {
  return isTenantOptionalPath(pathname)
}

export function withTenantInTo(to: To, preferredTenantId?: number | null): To {
  if (typeof to === 'string') {
    if (!to.startsWith('/') || shouldSkipTenantForPath(to)) return to
    return appendTenantIdToPath(to, preferredTenantId)
  }

  const next = { ...to }
  const pathname = typeof next.pathname === 'string' ? next.pathname : ''
  if (
    !pathname ||
    !pathname.startsWith('/') ||
    shouldSkipTenantForPath(pathname)
  ) {
    return next
  }
  next.pathname = appendTenantIdToPath(pathname, preferredTenantId)
  return next
}

export function navigateWithTenant(
  navigate: NavigateFunction,
  to: To | number,
  options?: NavigateOptions,
  preferredTenantId?: number | null
): void {
  if (typeof to === 'number') {
    navigate(to)
    return
  }
  navigate(withTenantInTo(to, preferredTenantId), options)
}

export function useTenantNavigate(
  preferredTenantId?: number | null
): NavigateFunction {
  const navigate = useNavigate()
  return useCallback(
    ((to: To | number, options?: NavigateOptions) => {
      navigateWithTenant(navigate, to, options, preferredTenantId)
    }) as NavigateFunction,
    [navigate, preferredTenantId]
  )
}

/**
 * Internal links get the tenant segment; external/mailto/blob URLs pass through.
 * A same-origin ABSOLUTE url is internal too — callers that prepend
 * window.location.origin themselves used to bypass the tenant entirely and
 * opened a tenant-less tab.
 */
function withTenantForOpen(url: string): string {
  if (typeof url !== 'string') return url
  if (url.startsWith('/') && !url.startsWith('//')) {
    return appendTenantIdToPath(url)
  }
  if (/^https?:\/\//i.test(url)) {
    try {
      const u = new URL(url)
      if (u.origin !== window.location.origin) return url
      if (shouldSkipTenantForPath(u.pathname)) return url
      const tenantAware = appendTenantIdToPath(
        `${u.pathname}${u.search}${u.hash}`
      )
      return `${u.origin}${tenantAware}`
    } catch {
      return url
    }
  }
  return url
}

export function openWithTenant(
  url: string,
  target?: string,
  features?: string
): Window | null {
  return window.open(withTenantForOpen(url), target, features)
}
